import { Clock, Download, Plus, RefreshCw, TrendingUp, Wallet } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import { CardSkeleton, SkeletonBlock } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { walletService } from "@/services/apiServices";
import type { AdminWalletRow, TopUpRequest, WalletTxn } from "@/types/wallet";
import { DEFAULT_RANGE, formatDate, formatINR } from "@/pages/retailer/wallet/data";
import WalletsTab, { type WalletRow } from "./components/WalletsTab";
import TopupRequestsTab from "./components/TopupRequestsTab";
import TransactionsTab from "./components/TransactionsTab";
import AddBalanceModal from "./components/AddBalanceModal";
import DeductBalanceModal from "./components/DeductBalanceModal";
import ReviewTopupModal from "./components/ReviewTopupModal";
import TopupDetailModal from "./components/TopupDetailModal";
import { tierFromStatus } from "./data";

interface AdminWalletStats {
  totalBalance: number;
  lifetimeRevenue: number;
  pendingCount: number;
  pendingValue: number;
}

const EMPTY_STATS: AdminWalletStats = { totalBalance: 0, lifetimeRevenue: 0, pendingCount: 0, pendingValue: 0 };

function todayStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function rangeToDates(range: string): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  switch (range) {
    case "Today":
      return { dateFrom: todayStr(now), dateTo: todayStr(now) };
    case "Last 7 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 7);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 30 Days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 30);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "This Month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    case "Last 3 Months": {
      const from = new Date(now);
      from.setMonth(from.getMonth() - 3);
      return { dateFrom: todayStr(from), dateTo: todayStr(now) };
    }
    default:
      return {};
  }
}

function escapeCsv(value: unknown): string {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(rows: (string | number)[][], filename: string) {
  const csv = rows.map((r) => r.map(escapeCsv).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function WalletStatCard({
  label,
  value,
  icon: Icon,
  iconBg,
  iconColor,
  sub,
  onClick,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  sub: string;
  onClick?: () => void;
}) {
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`rounded-[10px] border border-line bg-card p-5 shadow-card transition-all ${
        isClickable
          ? "cursor-pointer hover:border-primary-400 hover:shadow-md hover:-translate-y-1 active:translate-y-0"
          : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[13px] text-ink-secondary">{label}</span>
          <span className="text-[30px] font-bold leading-tight text-ink">{value}</span>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mt-3 text-xs text-ink-muted">{sub}</p>
    </div>
  );
}

export default function AdminWallets() {
  return (
    <ToastProvider>
      <WalletsInner />
    </ToastProvider>
  );
}

function WalletsInner() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<"wallets" | "topup" | "transactions">("wallets");

  const [wallets, setWallets] = useState<AdminWalletRow[]>([]);
  const [stats, setStats] = useState<AdminWalletStats>(EMPTY_STATS);
  const [retailerOptions, setRetailerOptions] = useState<{ value: string; label: string }[]>([]);

  const [wSearch, setWSearch] = useState("");
  const [balance, setBalance] = useState("");

  const [topups, setTopups] = useState<TopUpRequest[]>([]);
  const [topupTotal, setTopupTotal] = useState(0);
  const [topupPage, setTopupPage] = useState(1);
  const [topupPageSize, setTopupPageSize] = useState(10);
  const [topupLoading, setTopupLoading] = useState(true);
  const [tSearchInput, setTSearchInput] = useState("");
  const [tSearch, setTSearch] = useState("");
  const [tStatus, setTStatus] = useState("PENDING");
  const [tRetailer, setTRetailer] = useState("");
  const [tRange, setTRange] = useState(DEFAULT_RANGE);
  const [tRangeOpen, setTRangeOpen] = useState(false);

  const [txns, setTxns] = useState<WalletTxn[]>([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const [txnPageSize, setTxnPageSize] = useState(25);
  const [txnLoading, setTxnLoading] = useState(true);
  const [xSearchInput, setXSearchInput] = useState("");
  const [xSearch, setXSearch] = useState("");
  const [xRetailer, setXRetailer] = useState("");
  const [xType, setXType] = useState("");
  const [xRange, setXRange] = useState(DEFAULT_RANGE);
  const [xRangeOpen, setXRangeOpen] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [deductOpen, setDeductOpen] = useState(false);
  const [addTarget, setAddTarget] = useState<string | null>(null);
  const [deductTarget, setDeductTarget] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<TopUpRequest | null>(null);
  const [detailTarget, setDetailTarget] = useState<TopUpRequest | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setTSearch(tSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [tSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => setXSearch(xSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [xSearchInput]);

  useEffect(() => {
    setTopupPage(1);
  }, [tSearch, tStatus, tRetailer, tRange]);

  useEffect(() => {
    setTxnPage(1);
  }, [xSearch, xRetailer, xType, xRange]);

  const loadSummary = useCallback(
    async (silent = false) => {
      if (silent) setRefreshing(true);
      try {
        const res = await walletService.getAdminSummary();
        const data = res?.data;
        setWallets(data?.wallets ?? []);
        setStats(data?.stats ?? EMPTY_STATS);
        setRetailerOptions(data?.retailers ?? []);
        if (typeof data?.stats?.pendingCount === "number") {
          window.dispatchEvent(
            new CustomEvent("mandatepro_wallet_badge_updated", {
              detail: { pendingCount: data.stats.pendingCount },
            })
          );
        }
      } catch (err: any) {
        toast.error(err?.message || "Failed to load retailer wallets");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [toast]
  );

  const loadTopups = useCallback(async () => {
    setTopupLoading(true);
    try {
      const { dateFrom, dateTo } = rangeToDates(tRange);
      const res = await walletService.getAdminTopups({
        search: tSearch,
        status: tStatus,
        retailerId: tRetailer,
        dateFrom,
        dateTo,
        page: topupPage,
        limit: topupPageSize,
      });
      setTopups(res?.data?.topups ?? []);
      setTopupTotal(res?.data?.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load top-up requests");
      setTopups([]);
    } finally {
      setTopupLoading(false);
    }
  }, [tSearch, tStatus, tRetailer, tRange, topupPage, topupPageSize, toast]);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try {
      const { dateFrom, dateTo } = rangeToDates(xRange);
      const res = await walletService.getAdminTransactions({
        search: xSearch,
        retailerId: xRetailer,
        type: xType,
        dateFrom,
        dateTo,
        page: txnPage,
        limit: txnPageSize,
      });
      setTxns(res?.data?.transactions ?? []);
      setTxnTotal(res?.data?.pagination?.total ?? 0);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load transactions");
      setTxns([]);
    } finally {
      setTxnLoading(false);
    }
  }, [xSearch, xRetailer, xType, xRange, txnPage, txnPageSize, toast]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadTopups();
  }, [loadTopups]);

  useEffect(() => {
    loadTxns();
  }, [loadTxns]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadSummary(true), loadTopups(), loadTxns()]);
  }, [loadSummary, loadTopups, loadTxns]);

  const walletRows: WalletRow[] = wallets.filter((w) => {
    const q = wSearch.trim().toLowerCase();
    if (q && !`${w.retailerId} ${w.name} ${w.shopName}`.toLowerCase().includes(q)) return false;
    if (balance && tierFromStatus(w.status) !== balance) return false;
    return true;
  });

  async function addBalance(retailerId: string, amount: number, reason: string, remark: string) {
    try {
      const res = await walletService.adjustBalance({
        retailerId,
        type: "CREDIT",
        amount,
        reason: remark || reason,
      });
      toast.success(res?.message || "Balance added to wallet");
      setAddOpen(false);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not add balance");
    }
  }

  async function deductBalance(retailerId: string, amount: number, reason: string, remark: string) {
    try {
      const res = await walletService.adjustBalance({
        retailerId,
        type: "DEBIT",
        amount,
        reason: remark || reason,
      });
      toast.success(res?.message || "Balance deducted from wallet");
      setDeductOpen(false);
      await reloadAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not deduct balance");
    }
  }

  async function approveTopup(request: TopUpRequest, amountToCredit: number, remark: string) {
    const res = await walletService.reviewTopup({
      requestId: request.requestId,
      action: "APPROVE",
      amount: amountToCredit,
      remark,
    });
    toast.success(res?.message || "Top-up approved and wallet credited");
    setReviewTarget(null);
    await reloadAll();
  }

  async function rejectTopup(request: TopUpRequest, remark: string) {
    const res = await walletService.reviewTopup({
      requestId: request.requestId,
      action: "REJECT",
      remark,
    });
    toast.success(res?.message || "Top-up request rejected");
    setReviewTarget(null);
    await reloadAll();
  }

  async function exportWallets() {
    if (wallets.length === 0) {
      toast.error("No wallets to export");
      return;
    }
    setExporting(true);
    try {
      const headers = [
        "Retailer ID",
        "Shop/Business Name",
        "Owner",
        "Mobile",
        "Balance",
        "Lifetime Credited",
        "Lifetime Spent",
        "Pending Top-ups",
        "Status",
      ];
      const body = wallets.map((w) => [
        w.retailerId,
        w.shopName,
        w.name,
        w.mobile,
        w.balance,
        w.lifetimeCredited,
        w.lifetimeSpent,
        w.pendingTopups,
        w.status,
      ]);
      downloadCsv([headers, ...body], `retailer-wallets-${Date.now()}.csv`);
      toast.success(`Exported ${wallets.length} wallet${wallets.length === 1 ? "" : "s"}`);
    } finally {
      setExporting(false);
    }
  }

  async function exportTransactions() {
    try {
      const { dateFrom, dateTo } = rangeToDates(xRange);
      const res = await walletService.getAdminTransactions({
        search: xSearch,
        retailerId: xRetailer,
        type: xType,
        dateFrom,
        dateTo,
        limit: 10000,
      });
      const rows: WalletTxn[] = res?.data?.transactions ?? [];
      if (rows.length === 0) {
        toast.error("No transactions to export");
        return;
      }
      const headers = [
        "Date",
        "Transaction ID",
        "Retailer",
        "Category",
        "Description",
        "Reference",
        "Type",
        "Amount",
        "Balance After",
      ];
      const body = rows.map((t) => [
        formatDate(t.createdAt),
        t.txnId,
        t.shopName ?? t.retailerId,
        t.category,
        t.description,
        t.refNo ?? "",
        t.type,
        t.amount,
        t.balanceAfter,
      ]);
      downloadCsv([headers, ...body], `wallet-transactions-${Date.now()}.csv`);
      toast.success(`Exported ${rows.length} transaction${rows.length === 1 ? "" : "s"}`);
    } catch (err: any) {
      toast.error(err?.message || "Could not export transactions");
    }
  }

  function resetWalletFilters() {
    setWSearch("");
    setBalance("");
  }

  function resetTopupFilters() {
    setTSearchInput("");
    setTStatus("");
    setTRetailer("");
    setTRange(DEFAULT_RANGE);
  }

  function resetTxnFilters() {
    setXSearchInput("");
    setXRetailer("");
    setXType("");
    setXRange(DEFAULT_RANGE);
  }

  const tabBtn = "whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer";

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Retailer Wallets</h1>
          <p className="mt-1 text-sm text-ink-secondary">Monitor balances and manage retailer wallet recharges</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => reloadAll()}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={exportWallets}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
          <Button
            onClick={() => {
              setAddTarget(null);
              setAddOpen(true);
            }}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Balance
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <CardSkeleton cards={4} />
          <SkeletonBlock className="h-14 w-full rounded-[10px]" />
          <SkeletonBlock className="h-96 w-full rounded-[10px]" />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <WalletStatCard
              label="Total Wallet Balance"
              value={formatINR(stats.totalBalance)}
              icon={Wallet}
              iconBg="bg-[#EFF6FF]"
              iconColor="text-[#1E40AF]"
              sub="Across all retailers"
              onClick={() => setTab("wallets")}
            />
            <WalletStatCard
              label="Platform Revenue"
              value={formatINR(stats.lifetimeRevenue)}
              icon={TrendingUp}
              iconBg="bg-[#ECFDF5]"
              iconColor="text-[#059669]"
              sub="Lifetime charges collected"
              onClick={() => setTab("transactions")}
            />
            <WalletStatCard
              label="Pending Top-ups"
              value={`${stats.pendingCount}`}
              icon={Clock}
              iconBg="bg-[#F0F9FF]"
              iconColor="text-[#0284C7]"
              sub={`${formatINR(stats.pendingValue)} awaiting review`}
              onClick={() => setTab("topup")}
            />
          </div>

          <div className="rounded-[10px] border border-line bg-card p-1 shadow-card">
            <div className="flex gap-1 overflow-x-auto">
              <button
                type="button"
                onClick={() => setTab("wallets")}
                className={`${tabBtn} ${tab === "wallets" ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-page hover:text-ink"}`}
              >
                Wallets
              </button>
              <button
                type="button"
                onClick={() => setTab("topup")}
                className={`${tabBtn} ${tab === "topup" ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-page hover:text-ink"}`}
              >
                Top-up Requests
                {stats.pendingCount > 0 && (
                  <span
                    className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                      tab === "topup" ? "bg-white/20 text-white" : "bg-danger text-white"
                    }`}
                  >
                    {stats.pendingCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setTab("transactions")}
                className={`${tabBtn} ${tab === "transactions" ? "bg-primary-500 text-white" : "text-ink-secondary hover:bg-page hover:text-ink"}`}
              >
                Transactions
              </button>
            </div>
          </div>

          {tab === "wallets" && (
            <WalletsTab
              loading={loading}
              rows={walletRows}
              search={wSearch}
              onSearch={setWSearch}
              balance={balance}
              onBalance={setBalance}
              onReset={resetWalletFilters}
              onOpenLedger={(id) => navigate(`/admin/wallets/${id}`)}
              onAddBalance={(id) => {
                setAddTarget(id);
                setAddOpen(true);
              }}
              onDeductBalance={(id) => {
                setDeductTarget(id);
                setDeductOpen(true);
              }}
              onViewRetailer={(id) => navigate(`/admin/retailers/${id}`)}
            />
          )}
          {tab === "topup" && (
            <TopupRequestsTab
              loading={topupLoading}
              search={tSearchInput}
              onSearch={setTSearchInput}
              status={tStatus}
              onStatus={setTStatus}
              retailer={tRetailer}
              onRetailer={setTRetailer}
              range={tRange}
              onRange={setTRange}
              rangeOpen={tRangeOpen}
              onRangeOpen={setTRangeOpen}
              onReset={resetTopupFilters}
              rows={topups}
              retailerOptions={retailerOptions}
              page={topupPage}
              pageSize={topupPageSize}
              total={topupTotal}
              onPageChange={setTopupPage}
              onPageSizeChange={(n) => {
                setTopupPageSize(n);
                setTopupPage(1);
              }}
              onReview={(r) => setReviewTarget(r)}
              onViewDetail={(r) => setDetailTarget(r)}
            />
          )}
          {tab === "transactions" && (
            <TransactionsTab
              loading={txnLoading}
              search={xSearchInput}
              onSearch={setXSearchInput}
              retailer={xRetailer}
              onRetailer={setXRetailer}
              type={xType}
              onType={setXType}
              range={xRange}
              onRange={setXRange}
              rangeOpen={xRangeOpen}
              onRangeOpen={setXRangeOpen}
              onReset={resetTxnFilters}
              rows={txns}
              retailerOptions={retailerOptions}
              page={txnPage}
              pageSize={txnPageSize}
              total={txnTotal}
              onPageChange={setTxnPage}
              onPageSizeChange={(n) => {
                setTxnPageSize(n);
                setTxnPage(1);
              }}
              openMandate={(refNo) => navigate(`/admin/mandates/${refNo}`)}
              showRetailerFilter
              showRetailerColumn
              showCreatedBy
              showExport
              onExport={exportTransactions}
            />
          )}
        </div>
      )}

      <AddBalanceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        wallets={wallets}
        targetRetailerId={addTarget}
        onAdd={addBalance}
      />
      <DeductBalanceModal
        open={deductOpen}
        onClose={() => setDeductOpen(false)}
        wallets={wallets}
        targetRetailerId={deductTarget}
        onDeduct={deductBalance}
      />
      <ReviewTopupModal
        open={!!reviewTarget}
        onClose={() => setReviewTarget(null)}
        request={reviewTarget}
        onApprove={approveTopup}
        onReject={rejectTopup}
      />
      <TopupDetailModal open={!!detailTarget} onClose={() => setDetailTarget(null)} request={detailTarget} />
    </div>
  );
}
